import { hexToRgba, withoutExtension } from './color';
import type { ExportSettings, FrameSettings, PhotoAsset, RenderOptions } from '../types';

const ASPECTS: Record<string, number> = {
  '1:1': 1,
  '4:3': 4 / 3,
  '16:9': 16 / 9,
  '3:2': 3 / 2,
  '2:3': 2 / 3,
  '9:16': 9 / 16
};

function aspectValue(settings: FrameSettings, image: PhotoAsset): number {
  if (settings.aspectRatio === 'original') return image.width / image.height;
  if (settings.aspectRatio === 'custom') {
    return Math.max(0.05, settings.customAspectWidth) / Math.max(0.05, settings.customAspectHeight);
  }
  return ASPECTS[settings.aspectRatio] ?? image.width / image.height;
}

function roundedPath(
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number,
  width: number,
  height: number,
  radii: { tl: number; tr: number; br: number; bl: number }
): void {
  const max = Math.min(width, height) / 2;
  const tl = Math.min(radii.tl, max);
  const tr = Math.min(radii.tr, max);
  const br = Math.min(radii.br, max);
  const bl = Math.min(radii.bl, max);

  ctx.beginPath();
  ctx.moveTo(x + tl, y);
  ctx.lineTo(x + width - tr, y);
  ctx.quadraticCurveTo(x + width, y, x + width, y + tr);
  ctx.lineTo(x + width, y + height - br);
  ctx.quadraticCurveTo(x + width, y + height, x + width - br, y + height);
  ctx.lineTo(x + bl, y + height);
  ctx.quadraticCurveTo(x, y + height, x, y + height - bl);
  ctx.lineTo(x, y + tl);
  ctx.quadraticCurveTo(x, y, x + tl, y);
  ctx.closePath();
}

function metadataLines(asset: PhotoAsset, settings: FrameSettings): string[] {
  if (!settings.metadata.enabled) return [];
  const lines: string[] = [];
  const exif = asset.exif;

  if (settings.metadata.customText.trim()) lines.push(settings.metadata.customText.trim());
  if (settings.metadata.showCamera && exif.camera) lines.push(exif.camera);
  if (settings.metadata.showLens && exif.lens) lines.push(exif.lens);

  if (settings.metadata.showExposure) {
    const exposure = [exif.focalLength, exif.aperture, exif.shutter, exif.iso ? `ISO ${exif.iso}` : undefined].filter(Boolean);
    if (exposure.length) lines.push(exposure.join('  '));
  }

  if (settings.metadata.showDate && exif.date) lines.push(exif.date);
  if (settings.metadata.showLocation && exif.location) lines.push(exif.location);

  return lines;
}

function fitCanvasSize(baseWidth: number, baseHeight: number, options: RenderOptions): { width: number; height: number; scale: number } {
  if (options.width && options.height) {
    return { width: options.width, height: options.height, scale: Math.min(options.width / baseWidth, options.height / baseHeight) };
  }

  if (options.maxLongEdge) {
    const longEdge = Math.max(baseWidth, baseHeight);
    const scale = options.maxLongEdge / longEdge;
    return { width: Math.round(baseWidth * scale), height: Math.round(baseHeight * scale), scale };
  }

  return { width: Math.round(baseWidth), height: Math.round(baseHeight), scale: 1 };
}

export function renderFramedCanvas(asset: PhotoAsset, settings: FrameSettings, options: RenderOptions = {}): HTMLCanvasElement {
  const borderPx =
    settings.borderUnit === 'px'
      ? settings.borderWidth
      : Math.max(asset.width, asset.height) * (settings.borderWidth / 100);
  const minWidth = asset.width + borderPx * 2;
  const minHeight = asset.height + borderPx * 2;
  const ratio = aspectValue(settings, asset);

  let baseWidth = minWidth;
  let baseHeight = minHeight;

  if (baseWidth / baseHeight < ratio) {
    baseWidth = baseHeight * ratio;
  } else {
    baseHeight = baseWidth / ratio;
  }

  const fitted = fitCanvasSize(baseWidth, baseHeight, options);
  const scale = fitted.scale;
  const canvas = document.createElement('canvas');
  canvas.width = Math.max(1, fitted.width);
  canvas.height = Math.max(1, fitted.height);

  const ctx = canvas.getContext('2d');
  if (!ctx) return canvas;

  ctx.imageSmoothingEnabled = true;
  ctx.imageSmoothingQuality = 'high';
  ctx.scale(scale, scale);

  if (options.forceOpaqueBackground) {
    ctx.fillStyle = '#f8f8f6';
    ctx.fillRect(0, 0, baseWidth, baseHeight);
  }

  ctx.fillStyle = hexToRgba(settings.borderColor, settings.borderOpacity / 100);
  ctx.fillRect(0, 0, baseWidth, baseHeight);

  const imageX = (baseWidth - asset.width) / 2;
  const imageY = (baseHeight - asset.height) / 2;
  const linkedRadius = settings.corners.enabled ? settings.corners.radius : 0;
  const radii = settings.corners.linked
    ? { tl: linkedRadius, tr: linkedRadius, br: linkedRadius, bl: linkedRadius }
    : {
        tl: settings.corners.enabled ? settings.corners.topLeft : 0,
        tr: settings.corners.enabled ? settings.corners.topRight : 0,
        br: settings.corners.enabled ? settings.corners.bottomRight : 0,
        bl: settings.corners.enabled ? settings.corners.bottomLeft : 0
      };

  if (settings.shadow.enabled && settings.shadow.strength > 0) {
    ctx.save();
    ctx.shadowColor = hexToRgba(settings.shadow.color, settings.shadow.strength / 100);
    ctx.shadowBlur = settings.shadow.blur;
    ctx.shadowOffsetX = settings.shadow.offsetX;
    ctx.shadowOffsetY = settings.shadow.offsetY;
    ctx.fillStyle = 'rgba(20, 22, 28, 0.08)';
    roundedPath(ctx, imageX, imageY, asset.width, asset.height, radii);
    ctx.fill();
    ctx.restore();
  }

  ctx.save();
  roundedPath(ctx, imageX, imageY, asset.width, asset.height, radii);
  ctx.clip();
  ctx.drawImage(asset.image, imageX, imageY, asset.width, asset.height);
  ctx.restore();

  const lines = metadataLines(asset, settings);
  if (lines.length) {
    const padding = Math.max(24, borderPx * 0.32);
    const fontSize = settings.metadata.fontSize;
    const lineHeight = fontSize * 1.28;
    const xLeft = padding;
    const xRight = baseWidth - padding;
    const isRight = settings.metadata.position.endsWith('right');
    const isTop = settings.metadata.position.startsWith('top');
    const yStart = isTop ? padding + fontSize : baseHeight - padding - lineHeight * (lines.length - 1);

    ctx.save();
    ctx.font = `500 ${fontSize}px ${settings.metadata.fontFamily}`;
    ctx.fillStyle = settings.metadata.color;
    ctx.textAlign = isRight ? 'right' : 'left';
    ctx.textBaseline = 'alphabetic';
    lines.forEach((line, index) => {
      ctx.fillText(line, isRight ? xRight : xLeft, yStart + index * lineHeight, baseWidth - padding * 2);
    });
    ctx.restore();
  }

  return canvas;
}

export function exportFileName(asset: PhotoAsset | null, exportSettings: ExportSettings): string {
  const fallback = asset ? `${withoutExtension(asset.fileName)}_framed` : 'lumaframe_photo';
  const base = exportSettings.fileName.trim() || fallback;
  const extension = exportSettings.format === 'image/png' ? 'png' : 'jpg';
  return `${base.replace(/\.(png|jpe?g)$/i, '')}.${extension}`;
}

export function renderForExport(asset: PhotoAsset, frame: FrameSettings, exportSettings: ExportSettings): HTMLCanvasElement {
  if (exportSettings.sizeMode === 'preset') {
    return renderFramedCanvas(asset, frame, {
      maxLongEdge: exportSettings.presetLongEdge,
      forceOpaqueBackground: exportSettings.format === 'image/jpeg'
    });
  }

  if (exportSettings.sizeMode === 'custom') {
    return renderFramedCanvas(asset, frame, {
      width: exportSettings.customWidth,
      height: exportSettings.customHeight,
      forceOpaqueBackground: exportSettings.format === 'image/jpeg'
    });
  }

  return renderFramedCanvas(asset, frame, {
    forceOpaqueBackground: exportSettings.format === 'image/jpeg'
  });
}

export function canvasToBlob(canvas: HTMLCanvasElement, settings: ExportSettings): Promise<Blob> {
  return new Promise((resolve, reject) => {
    canvas.toBlob(
      (blob) => {
        if (blob) resolve(blob);
        else reject(new Error('The browser could not export this canvas.'));
      },
      settings.format,
      settings.quality / 100
    );
  });
}
